// src/pages/EdicionTrakaphoto.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "../utils/axiosConfig";
import BarraPrincipal from "../Modulos/BarraPrincipal";
import { formattedGlobalDate } from "../utils/formattedDates";
import ModalEdicionTrakaPhoto from "../components/maestros/modals/modalEdicionTrakaPhoto";
import ModalBaseLg from "../components/modals/modal-base-lg";
import Swal from "sweetalert2";

const HEADERS = [
    { key: "Id", label: "ID", w: 90 },
    { key: "Usuario", label: "Usuario", w: 140 },
    { key: "Equipo", label: "Equipo", w: 120 },
    { key: "Placa", label: "Placa", w: 110 },
    { key: "Latitud", label: "Latitud", w: 140 },
    { key: "Longitud", label: "Longitud", w: 140 },
    { key: "Fecha", label: "Fecha", w: 170 },
    { key: "Evento", label: "Evento", w: 220 },
    { key: "Foto", label: "Foto", w: 80 },
];

const TABLE_COLUMNS = [...HEADERS, { key: "__actions", label: "", w: 80 }];
const MIN_TABLE_WIDTH = TABLE_COLUMNS.reduce((acc, col) => acc + (col.w || 120), 0);

const LazyImage = ({ src, alt, className, width, height }) => {
    const [inView, setInView] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const rootRef = useRef(null);
    const isAuto = !height;

    useEffect(() => {
        const node = rootRef.current;
        if (!node) return;
        if (!("IntersectionObserver" in window)) {
            setInView(true);
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setInView(true);
                        observer.disconnect();
                    }
                });
            },
            { rootMargin: "200px" }
        );

        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    return (
        <div
            ref={rootRef}
            className={`lazy-img ${isAuto ? "auto" : ""} ${loaded ? "is-loaded" : ""} ${className || ""}`}
            style={{ width, height }}
        >
            {inView ? (
                <img
                    src={src}
                    alt={alt}
                    loading="lazy"
                    decoding="async"
                    onLoad={() => setLoaded(true)}
                    onError={() => setLoaded(true)}
                />
            ) : null}
            {!loaded && <div className="lazy-placeholder" aria-hidden="true" />}
        </div>
    );
};

const EdicionTrakaphoto = () => {
    // Filtros
    const [filters, setFilters] = useState({
        dispositivo: "",
        placa: "",
        usuario: "",
        fecha: "",
    });

    const [infoFotos, setInfoFotos] = useState({});
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sortBy, setSortBy] = useState(null);
    const [sortDirection, setSortDirection] = useState("asc");
    const tableScrollRef = useRef(null);
    const [containerW, setContainerW] = useState(0);

    // Permisos
    const [bitEdit, setBitEdit] = useState(false);

    // Modal
    const [modalEdit, setModalEdit] = useState(false);

    // Helpers de ordenamiento
    const parseVal = (v) => {
        if (v == null) return null;
        const asNum = Number(v);
        if (!Number.isNaN(asNum) && String(v).trim() !== "") return asNum;
        const asTs = Date.parse(String(v).replace(" ", "T"));
        if (!Number.isNaN(asTs)) return asTs;
        return String(v).toLowerCase();
    };
    const cmp = (a, b) =>
        a === b ? 0 : a == null ? 1 : b == null ? -1 : a > b ? 1 : -1;

    const sortedRows = useMemo(() => {
        if (!sortBy) return rows;
        const dir = sortDirection === "asc" ? 1 : -1;
        return [...rows].sort(
            (r1, r2) => dir * cmp(parseVal(r1?.[sortBy]), parseVal(r2?.[sortBy]))
        );
    }, [rows, sortBy, sortDirection]);

    const handleSort = (key) => {
        if (sortBy === key)
            setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
        else {
            setSortBy(key);
            setSortDirection("asc");
        }
    };

    const renderSortIcon = (key) =>
        sortBy === key ? (sortDirection === "asc" ? "↑" : "↓") : null;

    const fixedTableWidth = useMemo(
        () => TABLE_COLUMNS.reduce((acc, col) => acc + (col.w || 120), 0),
        []
    );

    const scale = useMemo(() => {
        if (!containerW || containerW <= 1290) return 1;
        return containerW / fixedTableWidth;
    }, [containerW, fixedTableWidth]);

    const colStyle = (idx) => {
        const base = TABLE_COLUMNS[idx]?.w || 120;
        const w = Math.round(base * scale);
        return { width: w, minWidth: w, maxWidth: w, textAlign: "center" };
    };

    const scaledColumns = useMemo(
        () => TABLE_COLUMNS.map((col, idx) => ({ ...col, w: colStyle(idx).width })),
        [scale]
    );

    const tableWidth = useMemo(() => {
        if (!containerW) return fixedTableWidth;
        return Math.max(fixedTableWidth, Math.round(fixedTableWidth * scale));
    }, [containerW, fixedTableWidth, scale]);

    // Acciones
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFilters((f) => ({ ...f, [name]: value }));
    };

    const handleSearch = async () => {
        setLoading(true);
        try {
            const { dispositivo, placa, usuario, fecha } = filters;
            const data = await getPhotos(dispositivo, placa, usuario, fecha);

            const mapped = (Array.isArray(data) ? data : data?.items || []).map(
                (x, i) => {
                    const serverDateTime = x.Hora ?? "";

                    return {
                        Id: x.IDPhoto ?? i + 1,
                        Usuario: x.Usuario ?? x.user ?? "",
                        Equipo: x.Device ?? x.Dispositivo ?? "",
                        Placa: x.Descripcion ?? "",
                        Latitud: x.Latitud ?? "",
                        Longitud: x.Longitud ?? "",
                        Geocerca: x.Geocerca ?? "",
                        // Conserva la fecha/hora del servidor sin conversión UTC.
                        Fecha: formattedGlobalDate(serverDateTime) || serverDateTime,
                        Evento: x.Evento ?? "",
                        Foto: x.Photo ?? "",
                        _raw: x,
                    };
                }
            );

            setRows(mapped);
        } catch (err) {
            console.error(err);
            Swal.fire("Error", "Error cargando datos", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleClear = () => {
        setFilters({ dispositivo: "", placa: "", usuario: "", fecha: "" });
        setRows([]);
        setSortBy(null);
        setSortDirection("asc");
    };

    const getPhotos = async (dispositivo, placa, usuario, fecha) => {
        const payload = { device: dispositivo || "", fecha: fecha || "", usuario: usuario || "", placa: placa || "" };
        try {
            const res = await axios.post(
                `${process.env.REACT_APP_SERVER}/operaciones2/getfotostraka`,
                JSON.stringify(payload)
            );
            console.log("Esto son los contratos que tienen geocoerca asignada:");
            console.log({
                payload,
                result: res.data?.data?.recordset.find((x) => x.Geocerca === 0),
            })
            return res.data?.data?.recordset || [];
        } catch (error) {
            console.log("Error obteniendo fotos:", error);
            return [];
        }
    };

    // Cierre de modal -> refresco
    const onCloseModalEdit = async () => {
        setModalEdit(false);
        // Pequeño delay para asegurar desmontaje del modal
        setTimeout(() => {
            handleSearch();
        }, 300);
    };

    const onEdit = (item) => {
        setInfoFotos(item || {});
        setModalEdit(true);
    };

    // Permisos
    useEffect(() => {
        const HandlePageByRole = async (role) => {
            try {
                const res = await axios.post(
                    process.env.REACT_APP_SERVER + "/usuarios/getconfiguracionpagina",
                    JSON.stringify({ pagina: role }),
                    {
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${localStorage.getItem("tkn")}`,
                        },
                        withCredentials: true,
                    }
                );

                if (res.data?.success === false) {
                    window.location.href = "/";
                } else if (Array.isArray(res.data?.data) && res.data.data.length > 0) {
                    setBitEdit(!!res.data.data[0].bitEdit);
                }
            } catch (error) {
                console.error("Error permisos:", error);
            }
        };
        HandlePageByRole("27");
    }, []);

    useEffect(() => {
        const el = tableScrollRef.current;
        if (!el) return;
        const update = () => {
            const cw = el.clientWidth;
            setContainerW(cw > 0 ? cw : 0);
        };
        update();
        let ro;
        if ("ResizeObserver" in window) {
            ro = new ResizeObserver(update);
            ro.observe(el);
        }
        window.addEventListener("resize", update);
        return () => {
            window.removeEventListener("resize", update);
            if (ro) ro.disconnect();
        };
    }, [sortedRows.length, loading]);

    return (
        <>
            <div className="layout-wrapper layout-content-navbar">
                <div className="layout-container">
                    <div className="layout-page">
                        <BarraPrincipal />
                        <div className="content-wrapper">
                            <div className="container-fluid flex-grow-1 container-p-y">
                                <h4 className="mb-3 text-center">Edición Trakaphoto</h4>

                                {/* FILTROS */}
                                <div className="card mb-3">
                                    <div className="card-body">
                                        <div className="row align-items-end gx-filtros">
                                            <div className="col-12 col-sm-6 col-lg-2">
                                                <label className="form-label mb-1">Dispositivo</label>
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm"
                                                    name="dispositivo"
                                                    value={filters.dispositivo}
                                                    onChange={handleChange}
                                                    placeholder="IMEI / ID"
                                                    style={{ boxSizing: "border-box", height: "40px" }}
                                                />
                                            </div>

                                            <div className="col-12 col-sm-6 col-lg-2">
                                                <label className="form-label mb-1">Placa</label>
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm"
                                                    name="placa"
                                                    value={filters.placa}
                                                    onChange={handleChange}
                                                    placeholder="ABC123"
                                                    style={{ boxSizing: "border-box", height: "40px" }}
                                                />
                                            </div>

                                            <div className="col-12 col-sm-6 col-lg-2">
                                                <label className="form-label mb-1">Usuario</label>
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm"
                                                    name="usuario"
                                                    value={filters.usuario}
                                                    onChange={handleChange}
                                                    placeholder="usuario"
                                                    style={{ boxSizing: "border-box", height: "40px" }}
                                                />
                                            </div>

                                            <div className="col-12 col-sm-6 col-lg-2">
                                                <label className="form-label mb-1">Fecha</label>
                                                <input
                                                    type="date"
                                                    className="form-control form-control-sm"
                                                    name="fecha"
                                                    value={filters.fecha}
                                                    onChange={handleChange}
                                                    style={{ boxSizing: "border-box", height: "40px" }}
                                                />
                                            </div>

                                            <div className="col-12 col-lg-2">
                                                <div className="d-grid d-lg-flex gap-2 justify-content-lg-end">
                                                    <button
                                                        type="button"
                                                        className="btn btn-primary btn-sm"
                                                        onClick={handleSearch}
                                                        disabled={loading}
                                                    >
                                                        {loading ? "Buscando..." : "Buscar"}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-secondary btn-sm"
                                                        onClick={handleClear}
                                                    >
                                                        Limpiar
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* styled-jsx: gutters de filtros */}
                                    <style jsx>{`
                    .gx-filtros {
                      --bs-gutter-x: 1.25rem;
                      --bs-gutter-y: 0.75rem;
                    }
                    @media (min-width: 992px) {
                      .gx-filtros {
                        --bs-gutter-x: 2rem;
                      }
                    }
                  `}</style>
                                </div>

                                {/* LISTA RESPONSIVE */}
                                {/* Cards en pantallas pequeñas */}
                                <div className="d-lg-none">
                                    {loading && (
                                        <div className="photo-preloader" role="status" aria-live="polite">
                                            <div className="spinner" />
                                            <span>Cargando informacion...</span>
                                        </div>
                                    )}

                                    {!loading && sortedRows.length === 0 && (
                                        <div className="card mb-3">
                                            <div className="card-body text-center text-muted">
                                                Sin resultados.
                                            </div>
                                        </div>
                                    )}

                                    <div className="cards-grid">
                                        {sortedRows.map((item) => (
                                            <div className="card" key={item.Id}>
                                                <div className="card-body">
                                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                                        <div>
                                                            <div className="fw-semibold">
                                                                {item.Placa || "(sin placa)"}
                                                            </div>
                                                            <div className="text-muted small">
                                                                {item.Fecha || "-"}
                                                            </div>
                                                        </div>
                                                        <span className="badge bg-secondary">
                                                            ID {item.Id}
                                                        </span>
                                                    </div>

                                                    {item.Foto ? (
                                                        <LazyImage
                                                            className="img-fluid rounded mb-2"
                                                            src={`https://fotosbk1.nyc3.cdn.digitaloceanspaces.com/${item.Foto.substring(
                                                                0,
                                                                10
                                                            )}/${item.Foto}`}
                                                            alt="Foto"
                                                        />
                                                    ) : null}

                                                    <div className="row g-2 small">
                                                        <div className="col-6">
                                                            <div className="text-muted">Usuario</div>
                                                            <div className="fw-medium">{item.Usuario || "-"}</div>
                                                        </div>
                                                        <div className="col-6">
                                                            <div className="text-muted">Equipo</div>
                                                            <div className="fw-medium">{item.Equipo || "-"}</div>
                                                        </div>
                                                        <div className="col-6">
                                                            <div className="text-muted">Latitud</div>
                                                            <div className="fw-medium">{item.Latitud || "-"}</div>
                                                        </div>
                                                        <div className="col-6">
                                                            <div className="text-muted">Longitud</div>
                                                            <div className="fw-medium">{item.Longitud || "-"}</div>
                                                        </div>
                                                        <div className="col-12">
                                                            <div className="text-muted">Evento</div>
                                                            <div className="fw-medium">{item.Evento || "-"}</div>
                                                        </div>
                                                    </div>

                                                    <div className="d-flex justify-content-end mt-3">
                                                        <button
                                                            type="button"
                                                            className="btn btn-primary btn-sm btn-tada"
                                                            onClick={() => { if (bitEdit) onEdit(item); }}
                                                            disabled={!bitEdit}
                                                            aria-label="Editar"
                                                        >
                                                            <i className="bx bxs-pencil icon"></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <style jsx>{`
                    .cards-grid {
                      display: grid;
                      grid-template-columns: 1fr;
                      gap: 12px;
                    }
                    @media (min-width: 576px) {
                      .cards-grid {
                        grid-template-columns: 1fr 1fr;
                      }
                    }
                  `}</style>
                                </div>

                                {/* Tabla en pantallas grandes */}
                                <div className="card cardContainerTrafico d-none d-lg-block">
                                    <div
                                        ref={tableScrollRef}
                                        className="table-responsive text-nowrap"
                                        style={{
                                            height: "calc(100vh - 240px)",
                                            overflowY: "auto",
                                            overflowX: "auto",
                                        }}
                                    >
                                        {loading && (
                                            <div className="photo-preloader sticky" role="status" aria-live="polite">
                                                <div className="spinner" />
                                                <span>Cargando informacion...</span>
                                            </div>
                                        )}
                                        <div
                                            className="traka-header-row FondoHeaderTabla2"
                                            style={{
                                                gridTemplateColumns: scaledColumns
                                                    .map((col) => `${col.w || 120}px`)
                                                    .join(" "),
                                                width: tableWidth,
                                                minWidth: fixedTableWidth,
                                                ["--traka-cell-px"]: "0.5rem",
                                                ["--traka-cell-py"]: "0.3rem",
                                            }}
                                        >
                                            {scaledColumns.map((col, idx) => (
                                                <div
                                                    key={col.key}
                                                    className={`traka-header-cell ${col.key === "__actions" ? "action" : ""}`}
                                                    style={colStyle(idx)}
                                                    onClick={() => (col.key !== "__actions" ? handleSort(col.key) : null)}
                                                    role={col.key !== "__actions" ? "button" : "presentation"}
                                                    tabIndex={col.key !== "__actions" ? 0 : -1}
                                                    onKeyDown={(e) => {
                                                        if (col.key === "__actions") return;
                                                        handleSort(col.key);
                                                                                                            }}
                                                                                                        >
                                                                                                            {col.label}
                                                                                                            {col.key !== "__actions" ? ` ${renderSortIcon(col.key) || ""}` : ""}
                                                                                                        </div>
                                                                                                    ))}
                                                                                                </div>

                                                                                                <table
                                                                                                    id="tablaTrakaPhotos"
                                                                                                    className="table table-hover table-sm mb-0"
                                                                                                    style={{ width: tableWidth, minWidth: fixedTableWidth }}
                                                                                                >
                                                                                                    <colgroup>
                                                                                                        {scaledColumns.map((col) => (
                                                                                                            <col key={col.key} style={{ width: col.w || 120 }} />
                                                                                                        ))}
                                                                                                    </colgroup>
                                                                                                    <tbody className="table-border-bottom-0">
                                                                                                        {sortedRows.length === 0 ? (
                                                                                                            <tr>
                                                                                                                <td colSpan={HEADERS.length + 1} className="text-center text-muted py-4">
                                                                                                                    {loading ? (
                                                                                                                        <>
                                                                                                                            <div>
                                                                                                                                <span>Cargando informacion...</span>
                                                                                                                            </div>
                                                                                                                        </>
                                                                                                                    ) : "Sin resultados."}
                                                                                                                </td>
                                                                                                            </tr>
                                                                                                        ) : (
                                                                                                            sortedRows.map((item, idx) => (
                                                                                                                <tr key={item.Id ?? idx}>
                                                                                                                    <td style={colStyle(0)}>{item.Id}</td>
                                                                                                                    <td style={colStyle(1)}>{item.Usuario}</td>
                                                                                                                    <td style={colStyle(2)}>{item.Equipo}</td>
                                                                                                                    <td style={colStyle(3)}>{item.Placa}</td>
                                                                                                                    <td style={colStyle(4)}>{item.Latitud}</td>
                                                                                                                    <td style={colStyle(5)}>{item.Longitud}</td>
                                                                                                                    <td style={{ ...colStyle(6), whiteSpace: "nowrap" }}>{item.Fecha}</td>
                                                                                                                    <td style={colStyle(7)}>{item.Evento}</td>
                                                                                                                    <td className="text-center" style={colStyle(8)}>
                                                                                                                        {item.Foto ? (
                                                                                                                            <LazyImage
                                                                                                                                width={100}
                                                                                                                                height={"auto"}
                                                                                                                                src={`https://fotosbk1.nyc3.cdn.digitaloceanspaces.com/${item.Foto.substring(
                                                                                                                                    0,
                                                                                                                                    10
                                                                                                                                )}/${item.Foto}`}
                                                                                                                                alt="Foto"
                                                                                                                            />
                                                                                                                        ) : (
                                                                                                                            "-"
                                                                                                                        )}
                                                                                                                    </td>
                                                                                                                    <td className="text-center" style={colStyle(9)}>
                                                                                                                        {bitEdit ? (
                                                                                                                            <i 
                                                                                                                                className="bx bxs-pencil bx-tada-hover icon" 
                                                                                                                                onClick={() => onEdit(item)}
                                                                                                                                role="button"
                                                                                                                                tabIndex={0}
                                                                                                                                onKeyDown={(e) => {
                                                                                                                                    if (e.key === "Enter" || e.key === " ") onEdit(item);
                                                                                                                                }}
                                                                                                                                style={{ cursor: "pointer", color: "#006699" }}
                                                                                                                                aria-label="Editar"
                                                                                                                            />
                                                                                                                        ) : null}
                                                                                                                    </td>
                                                                                                                </tr>
                                                                                                            ))
                                                                                                        )}
                                                                                                    </tbody>
                                                                                                </table>
                                                                                            </div>
                                                                                        </div>

                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>

                                                                    {/* Modal edición */}
            <ModalBaseLg
                isOpen={modalEdit}
                onClose={onCloseModalEdit}
                title={"Edicion de fotos Trakaphoto"}
                childComponent={<ModalEdicionTrakaPhoto onClose={onCloseModalEdit} info={infoFotos} />}
            />

            <style jsx>{`
                .photo-preloader {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 12px 16px;
                    background: #f5f7fb;
                    border: 1px solid #e1e6ef;
                    border-radius: 10px;
                    margin: 8px 0 12px;
                    color: #4b5b6b;
                    font-size: 0.9rem;
                }
                .photo-preloader.sticky {
                    position: sticky;
                    top: 0;
                    z-index: 2;
                }
                .spinner {
                    width: 18px;
                    height: 18px;
                    border: 2px solid #c7d0de;
                    border-top-color: #2f80ed;
                    border-radius: 50%;
                    animation: spin 0.8s linear infinite;
                }
                .lazy-img {
                    position: relative;
                    display: inline-block;
                    width: 100%;
                    overflow: hidden;
                    border-radius: 6px;
                    background: #f1f4f8;
                }
                .lazy-img.auto {
                    min-height: 160px;
                }
                .lazy-img img {
                    display: block;
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }
                .lazy-img.auto img {
                    height: auto;
                    object-fit: contain;
                }
                .lazy-img.is-loaded .lazy-placeholder {
                    opacity: 0;
                }
                .lazy-placeholder {
                    position: absolute;
                    inset: 0;
                    background: linear-gradient(
                        90deg,
                        rgba(230, 235, 245, 0.9),
                        rgba(245, 248, 252, 0.9),
                        rgba(230, 235, 245, 0.9)
                    );
                    background-size: 200% 100%;
                    animation: shimmer 1.2s ease-in-out infinite;
                }
                @keyframes spin {
                    to {
                        transform: rotate(360deg);
                    }
                }
                @keyframes shimmer {
                    0% {
                        background-position: 0% 0%;
                    }
                    100% {
                        background-position: -200% 0%;
                    }
                }
                :global(.cardContainerTrafico) {
                    position: relative;
                    z-index: 0;
                    isolation: isolate;
                }
                :global(#tablaTrakaPhotos) {
                    position: relative;
                    z-index: 0;
                    border-collapse: separate !important;
                    border-spacing: 0 !important;
                    table-layout: fixed;
                }
                :global(.cardContainerTrafico .table-responsive) {
                    position: relative;
                    z-index: 0;
                    isolation: isolate;
                }
                .traka-header-row {
                    position: sticky;
                    top: 0;
                    z-index: 10;
                    display: grid;
                    align-items: center;
                    min-width: max-content;
                    width: fit-content;
                    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
                    box-sizing: border-box;
                }
                :global(body.modal-open) .traka-header-row {
                    z-index: 1;
                }
                .traka-header-cell {
                    color: #ffffff;
                    text-transform: uppercase;
                    font-size: 0.8rem;
                    font-weight: 600;
                    padding: var(--traka-cell-py, 0.3rem) var(--traka-cell-px, 0.5rem);
                    white-space: nowrap;
                    cursor: pointer;
                    user-select: none;
                    box-sizing: border-box;
                }
                .traka-header-cell.action {
                    cursor: default;
                }
                .btn-tada {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 6px;
                }
                .edit-icon {
                    font-size: 1.1rem;
                    line-height: 1;
                }
                .btn-tada:hover .edit-icon {
                    animation: tada 0.9s ease both;
                }
                @keyframes tada {
                    0% {
                        transform: scale3d(1, 1, 1);
                    }
                    10%, 20% {
                        transform: scale3d(0.95, 0.95, 0.95) rotate3d(0, 0, 1, -3deg);
                    }
                    30%, 50%, 70%, 90% {
                        transform: scale3d(1.05, 1.05, 1.05) rotate3d(0, 0, 1, 3deg);
                    }
                    40%, 60%, 80% {
                        transform: scale3d(1.05, 1.05, 1.05) rotate3d(0, 0, 1, -3deg);
                    }
                    100% {
                        transform: scale3d(1, 1, 1);
                    }
                }
                :global(#tablaTrakaPhotos tbody td),
                :global(#tablaTrakaPhotos thead th) {
                    box-sizing: border-box;
                }
                :global(#tablaTrakaPhotos tbody td) {
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    padding: var(--traka-cell-py, 0.3rem) var(--traka-cell-px, 0.5rem) !important;
                    text-align: center;
                }
                :global(#tablaTrakaPhotos tbody) {
                    position: relative;
                    z-index: 0;
                }
                :global(#tablaTrakaPhotos tbody td),
                :global(#tablaTrakaPhotos tbody td img),
                :global(#tablaTrakaPhotos tbody td .lazy-img) {
                    position: relative;
                    z-index: 0;
                }
            `}</style>
        </>
    );
};

export default EdicionTrakaphoto;
